import { type UserListQuery, userStatusSchema } from '@sahab/contracts'
import { z } from 'zod'

export const USER_SORT_FIELDS = ['name', 'email', 'lastLoginAt', 'createdAt'] as const
type UserSortField = (typeof USER_SORT_FIELDS)[number]
export type UserSort = UserSortField | `-${UserSortField}`

export const USER_PAGE_SIZES = [25, 50, 100] as const

export function isUserSort(value: string): value is UserSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (USER_SORT_FIELDS as readonly string[]).includes(field)
}

/** The users list lives in the URL; invalid values fall back to the default instead of failing. */
export const userSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof USER_PAGE_SIZES)[number] => (USER_PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().refine(isUserSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  status: userStatusSchema.optional().catch(undefined),
  roleId: z.uuid().optional().catch(undefined),
  /** Users whose home unit is this org unit. */
  orgUnitId: z.uuid().optional().catch(undefined),
})

export type UserSearch = z.infer<typeof userSearchSchema>

export type UserListParams = Omit<UserListQuery, 'page' | 'pageSize'> & { page: number; pageSize: number }

export function toUserListQuery(search: UserSearch): UserListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? USER_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    status: search.status,
    roleId: search.roleId,
    orgUnitId: search.orgUnitId,
  }
}
